import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/Brand";
import { ArrowUpRight, Check, Sparkles, Stethoscope, Scale, CalendarCheck2 } from "lucide-react";

const features = [
  { t: "Tu URL personalizada", d: "book-it.com.mx/tu-negocio. Comparte un solo link y tus clientes agendan en segundos." },
  { t: "Stripe a tu cuenta", d: "Conecta tu Stripe Secret Key — los depósitos van directo a ti, no a la plataforma." },
  { t: "Multi-staff con login", d: "Cada profesional con sus horarios, su agenda y su acceso. Slots se bloquean automáticamente." },
  { t: "POS integrado", d: "Combina servicios + productos en una venta. El depósito ya pagado se aplica como crédito." },
  { t: "Referidos = meses gratis", d: "Cada negocio que se registre con tu código te da 1 mes gratis acumulable." },
  { t: "30 días de trial", d: "Sin tarjeta. Configura todo a tu ritmo. $200 MXN/mes + $30 por profesional cuando inicies (IVA 16% incluido)." },
];

const useCases = [
  { id: "clinic", t: "Clínica & Dental", d: "Citas con cobro de cuota de consulta anticipado.", icon: Stethoscope },
  { id: "spa", t: "Spa & Belleza", d: "Reservas con depósito y venta de retail post-cita.", icon: Sparkles },
  { id: "consulting", t: "Consultoría & Legal", d: "Sesiones cobradas por hora o paquetes.", icon: Scale },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 glass-nav border-b border-slate-200/60">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="logo-home-link">
            <BrandLogo subtitle />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600">
            <a href="#features" className="hover:text-slate-900">Cómo funciona</a>
            <a href="#use-cases" className="hover:text-slate-900">Industrias</a>
            <a href="#pricing" className="hover:text-slate-900">Precio</a>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <Button variant="outline" onClick={() => navigate(user.role === "admin" ? "/admin" : user.role === "staff" ? "/staff/me" : "/dashboard")} data-testid="nav-go-app">
                Ir al panel
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn">Iniciar sesión</Button>
                <Button onClick={() => navigate("/signup")} data-testid="nav-signup-btn">Crear mi negocio</Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-20 pb-24 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 fade-in">
            <p className="overline mb-6" data-testid="hero-overline">Book-It · Plataforma SaaS de agendamiento</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.95] tracking-tighter text-slate-900">
              Tu negocio.<br />
              Tu URL.<br />
              <span className="italic font-light text-[hsl(var(--accent))]">Tu Stripe.</span>
            </h1>
            <p className="mt-8 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed">
              Lanza tu agenda online con URL personalizada (<code className="text-sm bg-slate-100 px-1.5 rounded">book-it.com.mx/tu-negocio</code>),
              cobra depósitos a tu cuenta Stripe, gestiona profesionales y vende productos en caja.
              30 días gratis.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Button size="lg" onClick={() => navigate("/signup")} data-testid="hero-signup-btn">
                Empezar gratis <ArrowUpRight className="ml-1 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/bookit")} data-testid="hero-demo-btn">
                Ver demo /bookit
              </Button>
            </div>
            <div className="mt-12 flex items-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2"><CalendarCheck2 className="h-4 w-4" /> Multi-staff con login</div>
              <div className="hidden sm:flex items-center gap-2"><Sparkles className="h-4 w-4" /> Cobros con tu Stripe</div>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-slate-200 relative bg-white">
              <img
                src="https://customer-assets.emergentagent.com/job_booking-template/artifacts/8y26ozli_book-it_person.png"
                alt="Cliente reservando una cita desde su celular"
                className="h-full w-full object-contain"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-md border border-slate-200 rounded-xl p-5">
                <p className="overline text-slate-500">URL en vivo</p>
                <p className="font-mono text-base mt-2" style={{ color: "#1E3A8A" }}>book-it.com.mx/<span className="font-bold">bookit</span></p>
                <p className="text-xs text-slate-600 mt-1">Solo comparte el link · sin descargas</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section id="use-cases" className="border-t border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24">
          <div className="grid md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-5">
              <p className="overline">Industrias</p>
              <h2 className="mt-4 text-3xl sm:text-4xl font-light tracking-tight">Para cualquier negocio que agenda.</h2>
            </div>
            <p className="md:col-span-6 md:col-start-7 text-slate-600 leading-relaxed">
              Configura tus servicios, horarios y profesionales una sola vez. Tus clientes reservan desde
              cualquier dispositivo. La plataforma se encarga del resto.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {useCases.map((b) => {
              const Icon = b.icon;
              return (
                <article key={b.id} className="rounded-xl border border-slate-200 bg-white p-8 card-hover" data-testid={`use-case-${b.id}`}>
                  <Icon className="h-6 w-6 text-[hsl(var(--accent))]" />
                  <h3 className="mt-4 font-display text-2xl">{b.t}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{b.d}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-slate-200 bg-slate-50/60">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24">
          <div className="mb-12">
            <p className="overline">Qué incluye</p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-light tracking-tight max-w-2xl">
              Toda la operación, en un solo panel.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f) => (
              <div key={f.t} className="rounded-xl border border-slate-200 bg-white p-6 card-hover">
                <h3 className="font-display text-lg flex items-start gap-2">
                  <Check className="h-5 w-5 text-[hsl(var(--accent))] shrink-0 mt-0.5" />
                  {f.t}
                </h3>
                <p className="mt-2 text-sm text-slate-600 ml-7">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t border-slate-200">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 py-24 text-center">
          <p className="overline">Precio simple</p>
          <h2 className="mt-4 text-4xl sm:text-5xl font-light tracking-tight">
            $200 <span className="text-2xl text-slate-500">MXN/mes</span>
          </h2>
          <p className="mt-3 text-slate-600">+ $30 MXN por cada profesional activo</p>
          <p className="mt-2 text-xs uppercase tracking-wider text-slate-500" data-testid="landing-iva-note">IVA (16%) incluido</p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="rounded-lg border border-slate-200 p-4">
              <p className="font-display text-2xl">30</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">Días de trial</p>
            </div>
            <div className="rounded-lg border border-slate-200 p-4">
              <p className="font-display text-2xl">7</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">Días de gracia</p>
            </div>
            <div className="rounded-lg p-4 text-white" style={{ backgroundColor: "#1E3A8A" }}>
              <p className="font-display text-2xl">1 mes</p>
              <p className="text-xs text-slate-300 uppercase tracking-wider mt-1">Gratis por referido</p>
            </div>
          </div>
          <div className="mt-10">
            <Button size="lg" onClick={() => navigate("/signup")} data-testid="pricing-signup-btn">
              Crear mi negocio · gratis 30 días
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-slate-500">
          <p>© {new Date().getFullYear()} Book-It · Smart Booking Platform</p>
          <p>Construido con FastAPI + React + MongoDB</p>
        </div>
      </footer>
    </div>
  );
}
